import React from "react";

import Box from "@mui/material/Box";

import ActiveFilters from "./ActiveFilters";
import FilterButton from "components/Filters/FilterButton";
import CheckboxFilter from "components/Filters/CheckboxFilter";
import DateRangeFilter from "components/Filters/DateRangeFilter";

import { FilterTypeEnum, FilterValue, TableFilter } from "./types";

interface TableFiltersProps<T> {
  activeFilters: T;
  filters: TableFilter<T>[];
  onFilterChange: (filterId: keyof T, activeFilter: FilterValue) => void;
  onClearFilters: () => void;
}

const TableFilters = <T extends Record<string, FilterValue>>(
  props: TableFiltersProps<T>
) => {
  const { activeFilters, filters, onFilterChange, onClearFilters } = props;

  const renderFilter = (filter: TableFilter<T>) => {
    const values = activeFilters[filter.id];

    switch (filter.type) {
      case FilterTypeEnum.TOGGLE:
        return (
          <FilterButton
            id={filter.id as string}
            label={filter.label}
            active={values}
            handleClick={() => onFilterChange(filter.id, !values)}
          >
            {filter.children}
          </FilterButton>
        );
      case FilterTypeEnum.CHECKBOX:
        return (
          <CheckboxFilter
            id={filter.id as string}
            label={filter.label}
            options={filter.options}
            activeFilter={values}
            emptyText={filter.emptyText}
            onChange={(newValues) => onFilterChange(filter.id, newValues)}
          />
        );
      case FilterTypeEnum.DATE_RANGE:
        return (
          <DateRangeFilter
            id={filter.id as string}
            label={filter.label}
            options={filter.options}
            activeFilter={values}
            onChange={(newValues) => onFilterChange(filter.id, newValues)}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: "100%", mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          overflowX: "auto",
          overflowY: "hidden",
        }}
      >
        {filters.map((filter) => (
          <Box key={filter.id as string}>{renderFilter(filter)}</Box>
        ))}
      </Box>
      <ActiveFilters
        activeFilters={activeFilters}
        filters={filters}
        onChange={onFilterChange}
        onClear={onClearFilters}
      />
    </Box>
  );
};

export default React.memo(TableFilters) as typeof TableFilters;
